import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { Box, Typography } from '@mui/material'
import { useIntl } from 'react-intl'
import { Navigate } from 'react-router-dom'
import RoutesEnum from '../../../components/Routes/rootEnum'
import SpinnerBlock from '../../../components/Spinner/SpinnerBlock'
import messages from './messages'

// E20 F20-7 (WI 794) — the public landing for the Microsoft admin-consent return leg
// (/federation-consent/:consentStateKey). Mirrors the /invitation/ pattern: rendered by AppShell inside
// MsalProvider but before the auth gate, because the browser arriving here is often the customer's IT
// admin with no LogikSpark session (the AO forwards the consent link). The consent-state poll is AO-gated,
// so the anonymous branch renders static copy only. E20 (story 20.4.3): every FAILING outcome now renders
// a server-side page at the callback instead, because that page can hand the administrator a reissued
// link to forward; only the completed round trip redirects here, and it carries no query parameters. An
// authenticated user (the AO who opened the consent link themselves) is bounced into the federation
// settings page, which reopens the wizard from the key.
const FederationConsentPage = ({ consentStateKey }: { consentStateKey: string }) => {
  const { formatMessage } = useIntl()
  const isAuthenticated = useIsAuthenticated()
  const { inProgress } = useMsal()

  // Same settling rule as AppShell's gate: while MSAL is still processing, neither branch is safe to pick.
  if (inProgress !== InteractionStatus.None) {
    return <SpinnerBlock />
  }

  if (isAuthenticated) {
    return (
      <Navigate
        to={`${RoutesEnum.FEDERATION}?consentStateKey=${encodeURIComponent(consentStateKey)}`}
        replace
      />
    )
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', mt: 10, px: 3, textAlign: 'center' }}>
      <Typography variant='h5' sx={{ mb: 2 }}>
        {formatMessage(messages.consentPageSuccessTitle)}
      </Typography>
      <Typography variant='body1' color='text.secondary'>
        {formatMessage(messages.consentPageSuccessBody)}
      </Typography>
    </Box>
  )
}

export default FederationConsentPage
